export interface NetworkStep {
  title: string;
  body: string;
}

export interface NetworkService {
  slug: string;
  eyebrow: string;
  navTitle: string;
  heroTitle: string;
  heroSubtitle: string;
  overviewTitle: string;
  overviewBody: string[];
  intro: string;
  capabilities: { title: string; body: string }[];
  howItWorks: NetworkStep[];
  governance: {
    title: string;
    body: string;
  };
  related: string[]; // slugs
}

export const networkServices: NetworkService[] = [
  {
    slug: "validation",
    eyebrow: "Clinical Validation",
    navTitle: "Clinical Validation",
    heroTitle: "Evidence where it matters most.",
    heroSubtitle:
      "Independent clinical validation of AI models in real institutional environments — the proof health systems and regulators take seriously.",
    overviewTitle: "Evidence where it matters most.",
    overviewBody: [
      "Independent clinical validation of AI models in real institutional environments — the proof health systems and regulators take seriously.",
      "Designed with academic centers, run on production infrastructure, published transparently.",
    ],
    intro:
      "Validation is where clinical AI either earns institutional trust or quietly disappears. We design, run, and publish independent studies on production infrastructure — so the evidence reflects the conditions under which the model will actually be used.",
    capabilities: [
      {
        title: "Prospective real-world studies",
        body: "Designed with academic and clinical partners, run on the same infrastructure used in deployment — not a sandbox, not a retrospective dataset alone.",
      },
      {
        title: "Multi-site, multi-scanner cohorts",
        body: "Evidence that holds up across institutions, vendors, and patient populations — the conditions regulators and procurement teams care about.",
      },
      {
        title: "Bias, equity, and drift analysis",
        body: "Subgroup performance, calibration over time, and post-deployment monitoring as part of the study design — not a footnote.",
      },
      {
        title: "Transparent, peer-reviewed reporting",
        body: "Pre-registered protocols where appropriate. Results published openly, including negative findings.",
      },
    ],
    howItWorks: [
      { title: "Scope", body: "Define the clinical question, endpoints, and the institutional context the model will operate in." },
      { title: "Design", body: "Co-author the protocol with clinical partners; align on governance, ethics, and data-sharing terms." },
      { title: "Run", body: "Execute on the deepc infrastructure with full audit trail, monitoring, and version control." },
      { title: "Report", body: "Publish methods and results transparently. Hand evidence to procurement, regulators, and clinical leadership." },
    ],
    governance: {
      title: "Aligned to the standards institutions are accountable to.",
      body: "Study design and reporting align with EU AI Act high-risk requirements, MDR post-market surveillance expectations, and institutional ethics frameworks. Health systems retain ownership of their data and findings throughout.",
    },
    related: ["governance", "research", "infrastructure"],
  },
  {
    slug: "commercialization",
    eyebrow: "Commercialization",
    navTitle: "Commercialization",
    heroTitle: "Bring clinical AI to the institutions that need it.",
    heroSubtitle:
      "A path to market built around the realities of healthcare procurement: governance, integration, validation, and trust.",
    overviewTitle: "Bring clinical AI to the institutions that need it.",
    overviewBody: [
      "A path to market for AI developers built around the realities of healthcare procurement: governance, integration, validation, and trust.",
      "Not a marketplace. A neutral commercial layer with the institutional credibility AI vendors lack on their own.",
    ],
    intro:
      "Most clinical AI fails commercially long before it fails clinically. Health systems do not buy models — they buy programs they can govern, integrate, and defend. We are the commercial layer that makes that purchase possible, on terms institutions recognize.",
    capabilities: [
      {
        title: "Institutional contracting",
        body: "Frameworks aligned with hospital procurement, legal, and compliance — not consumer-software terms repackaged.",
      },
      {
        title: "Integration and deployment readiness",
        body: "Models arrive on infrastructure already validated for the institutional environment. No without IT project per vendor.",
      },
      {
        title: "Neutral positioning",
        body: "We do not build the models. That neutrality is the reason health systems and AI developers can transact through us at all.",
      },
      {
        title: "Lifecycle commercial support",
        body: "Renewals, expansions, and multi-site rollouts handled as a single relationship — not a series of separate sales cycles.",
      },
    ],
    howItWorks: [
      { title: "Qualify", body: "Assess clinical fit, regulatory status, and integration readiness against institutional requirements." },
      { title: "Onboard", body: "Bring the model onto the deepc infrastructure — validated, governed, deployable." },
      { title: "Contract", body: "Single institutional contract covers infrastructure, model, governance, and support." },
      { title: "Scale", body: "Expand across sites, services, and use cases through the same commercial relationship." },
    ],
    governance: {
      title: "Institutional terms, by default.",
      body: "Contracts reflect EU AI Act obligations, MDR responsibilities, and the data-sovereignty terms that European health systems require. The institution is always the data controller.",
    },
    related: ["infrastructure", "rollout", "governance"],
  },
  {
    slug: "infrastructure",
    eyebrow: "Managed Infrastructure",
    navTitle: "Managed Infrastructure",
    heroTitle: "The operational backbone for clinical AI programs.",
    heroSubtitle:
      "Managed runtime, monitoring, and lifecycle services — so health systems can run an AI program without standing up an AI operations team from scratch.",
    overviewTitle: "The operational backbone for clinical AI programs.",
    overviewBody: [
      "Managed runtime, monitoring, and lifecycle services — so health systems can run an AI program without standing up an AI operations team from scratch.",
      "Predictable, secure, and built for multi-site reality.",
    ],
    intro:
      "Running clinical AI in production is an operations problem, not a model problem. We provide the managed infrastructure — runtime, monitoring, lifecycle, and support — so institutions can deploy and govern AI without rebuilding operations from scratch.",
    capabilities: [
      {
        title: "Managed runtime",
        body: "Models run on infrastructure designed for clinical SLAs — not generic cloud defaults.",
      },
      {
        title: "Continuous monitoring",
        body: "Performance, drift, calibration, and utilization tracked per site, per model, per cohort.",
      },
      {
        title: "Lifecycle management",
        body: "Versioning, controlled updates, rollback, and audit trail — every change traceable, every state reproducible.",
      },
      {
        title: "Multi-site operations",
        body: "One operating layer across hospitals, departments, and modalities. No per-site reinvention.",
      },
    ],
    howItWorks: [
      { title: "Provision", body: "Stand up the runtime in the institution's environment — on-prem, hybrid, or sovereign cloud." },
      { title: "Integrate", body: "Connect to PACS, EHR, and clinical workflow systems through validated interfaces." },
      { title: "Operate", body: "Monitor, update, and support as a managed service — institutional team stays in control, not on call." },
      { title: "Evolve", body: "Add models, sites, and use cases without rebuilding the operational layer." },
    ],
    governance: {
      title: "Built for the environments institutions actually run.",
      body: "Aligned with EU data-sovereignty requirements, MDR technical documentation expectations, and institutional information-security frameworks. The institution remains the data controller and the system owner.",
    },
    related: ["rollout", "governance", "validation"],
  },
  {
    slug: "rollout",
    eyebrow: "Rollout & Enablement",
    navTitle: "Rollout & Enablement",
    heroTitle: "From signed contract to clinical adoption.",
    heroSubtitle:
      "Structured rollout, clinician enablement, and change management — turning a deployed model into a used model.",
    overviewTitle: "From signed contract to clinical adoption.",
    overviewBody: [
      "Structured rollout, clinician enablement, and change management — turning a deployed model into a used model.",
      "Adoption is the metric. The network is built around it.",
    ],
    intro:
      "Deployment is not adoption. Most clinical AI sits unused inside the systems that bought it. Rollout & Enablement is the discipline of turning installed software into clinical practice — staged, measured, and owned alongside the institution.",
    capabilities: [
      {
        title: "Staged site rollout",
        body: "Sequenced introduction across departments and sites, with clinical champions identified and supported at each stage.",
      },
      {
        title: "Clinician enablement",
        body: "Training designed around clinical workflow, not software features. Built with the people who will use it.",
      },
      {
        title: "Change management",
        body: "Operational, communication, and governance support — the unglamorous work that determines whether adoption holds.",
      },
      {
        title: "Adoption measurement",
        body: "Utilization, clinical impact, and clinician sentiment tracked as program KPIs — not vanity metrics.",
      },
    ],
    howItWorks: [
      { title: "Plan", body: "Define adoption goals, sites, sequencing, and the clinical leadership accountable for each." },
      { title: "Prepare", body: "Workflow integration, clinician training, and governance touchpoints in place before go-live." },
      { title: "Launch", body: "Staged go-live with active support, daily monitoring, and rapid feedback loops." },
      { title: "Sustain", body: "Ongoing enablement, performance review, and program governance — adoption treated as a living metric." },
    ],
    governance: {
      title: "Adoption as a governed program, not a project.",
      body: "Rollout aligns with institutional clinical governance, MDR post-market obligations, and the operational requirements of the EU AI Act. Clinical leadership remains accountable; we make that accountability practical.",
    },
    related: ["governance", "infrastructure", "commercialization"],
  },
  {
    slug: "governance",
    eyebrow: "Governance & Trust",
    navTitle: "Governance & Trust",
    heroTitle: "Institutional-grade oversight, by default.",
    heroSubtitle:
      "Frameworks, processes, and operating practices aligned to EU AI Act, MDR, and institutional governance — embedded in the way the network operates, not bolted on.",
    overviewTitle: "Institutional-grade oversight, by default.",
    overviewBody: [
      "Frameworks, processes, and operating practices aligned to EU AI Act, MDR, and institutional governance — embedded in the way the network operates, not bolted on.",
      "Health systems remain accountable. The network makes accountability operable.",
    ],
    intro:
      "Health systems are accountable for the AI they deploy — to regulators, clinicians, and patients. Governance & Trust is how we make that accountability operable: frameworks, processes, and evidence built into the network, not assembled after the fact.",
    capabilities: [
      {
        title: "EU AI Act alignment",
        body: "Risk classification, technical documentation, post-market monitoring, and human-oversight processes mapped into the operating model.",
      },
      {
        title: "MDR-aware processes",
        body: "Quality management, vigilance, and post-market surveillance practices that hold up under medical-device scrutiny.",
      },
      {
        title: "Institutional oversight",
        body: "Decision rights, escalation paths, and clinical governance integration — the institution stays in charge.",
      },
      {
        title: "Auditability by design",
        body: "Every model version, decision boundary, monitoring signal, and update is logged, attributable, and reproducible.",
      },
    ],
    howItWorks: [
      { title: "Frame", body: "Map the institution's governance structure, regulatory posture, and clinical accountability lines." },
      { title: "Embed", body: "Configure infrastructure processes — approvals, monitoring, escalation — to the institutional model." },
      { title: "Operate", body: "Run governance as a continuous process, with evidence generated automatically as part of operations." },
      { title: "Report", body: "Provide regulators, leadership, and clinical governance bodies with the evidence they need, on demand." },
    ],
    governance: {
      title: "Compliance is the floor, not the goal.",
      body: "Aligned with EU AI Act, MDR, GDPR, and the institutional governance frameworks European health systems already operate under. The objective is operable trust — not paperwork.",
    },
    related: ["validation", "infrastructure", "research"],
  },
  {
    slug: "research",
    eyebrow: "Research",
    navTitle: "Research",
    heroTitle: "Where clinical AI is being defined.",
    heroSubtitle:
      "Collaborative research with academic centers and clinical partners on the questions the field has not yet answered.",
    overviewTitle: "Where clinical AI is being defined.",
    overviewBody: [
      "Collaborative research with academic centers and clinical partners on the questions the field has not yet answered: real-world performance, equity, drift, governance, and adoption.",
      "Public, peer-reviewed, and infrastructure-aware.",
    ],
    intro:
      "The hardest questions in clinical AI are not yet answered: how models behave at scale, how performance shifts over time, how equity holds across populations, how governance actually works in practice. Research is how we contribute to those answers — openly, with the institutions doing the work.",
    capabilities: [
      {
        title: "Real-world performance",
        body: "How models behave outside the conditions they were trained in — across sites, scanners, populations, and time.",
      },
      {
        title: "Equity and bias",
        body: "Subgroup performance, calibration, and outcome impact — studied as primary endpoints, not afterthoughts.",
      },
      {
        title: "Governance research",
        body: "What institutional oversight of AI actually looks like in practice — and what it should look like.",
      },
      {
        title: "Open publication",
        body: "Methods, code, and results published transparently. Negative results included.",
      },
    ],
    howItWorks: [
      { title: "Question", body: "Identify a question the field has not adequately answered — with clinical and academic partners." },
      { title: "Design", body: "Co-author the study; align on data, governance, and publication terms before any work begins." },
      { title: "Conduct", body: "Run on the deepc infrastructure with full reproducibility and auditability." },
      { title: "Publish", body: "Peer-reviewed publication with open methods. Findings inform the infrastructure and the field." },
    ],
    governance: {
      title: "Research conducted under institutional terms.",
      body: "Ethics approval, data-sharing agreements, and publication terms are agreed up front. Institutions retain ownership of their data and a voice in how findings are used.",
    },
    related: ["validation", "governance", "infrastructure"],
  },
];

export const getNetworkService = (slug: string) =>
  networkServices.find((s) => s.slug === slug);
